import { Fragment, useContext, useRef, useState } from 'react';
import { classnames } from 'helpers/utils';
import { HelperText } from 'common/helper-text';
import { Checkbox } from 'common/checkbox';
import { cloneDeep } from 'lodash';
import { Breakpoints } from 'common/enums';
import { useMediaQuery } from 'hooks/use-media-query';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { ChevronIcon, CrossIcon, FilterLinesIcon } from 'assets/icons';
import { Menu, PositionType } from 'common/menu';
import { useClickOutside } from 'hooks/click-outside';
import { BottomSheet } from 'common/bottom-sheet';
import { Badge } from 'common/badge';
import { CounterBadge } from 'common/counter-badge';
import {
  CollaboratorsContext,
  collaboratorsActions,
} from 'context/collaborators-context';
import { CollaboratorsQueryParams } from 'models/collaborators-query-params';
import { LocalStorageApi } from 'helpers/local-storage';
import styles from './collaborators-filters.module.scss';

type FiltersGroupsProps = {
  checkBoxFilters: CheckboxGroupType[];
  selectUniqueFilter: (
    checkBoxGroupSelectedId: string,
    filterSelected: string,
  ) => void;
  visibleFilters: boolean[];
  setVisibleFilters: (visibleFilters: boolean[]) => void;
  isAnyFilterApplied: boolean;
};

const FiltersGroups: React.FC<FiltersGroupsProps> = ({
  checkBoxFilters,
  selectUniqueFilter,
  visibleFilters,
  setVisibleFilters,
  isAnyFilterApplied,
}) => (
  <div className={styles.filterGroups}>
    {isAnyFilterApplied && (
      <div className={styles.filtersChips}>
        {checkBoxFilters.map((group) =>
          group.options.map((filter) =>
            filter.isSelected ? (
              <Badge
                key={filter.id.concat(filter.name)}
                className={classnames(
                  'text__body__regular__tiny__gray700',
                  styles.filterChip,
                )}
                message={filter.name}
                closable
                onPressClose={() => selectUniqueFilter(group.id, filter.id)}
              />
            ) : null,
          ),
        )}
      </div>
    )}
    {checkBoxFilters.map((checkBoxGroup, index) => (
      <Fragment key={checkBoxGroup.id}>
        <button
          type="button"
          className={styles.filterGroup}
          onClick={() => {
            const newVisibleFilters = visibleFilters.slice();
            newVisibleFilters[index] = !newVisibleFilters[index];
            setVisibleFilters(newVisibleFilters);
          }}
        >
          <span className="text__body__bold__small__gray800">
            {checkBoxGroup.name}
          </span>
          <ChevronIcon
            className={classnames(styles.chevron, {
              [styles.menuOpen]: visibleFilters[index],
            })}
          />
        </button>
        {visibleFilters[index] && (
          <div className={styles.filterGroupOptions} key={checkBoxGroup.id}>
            {checkBoxGroup.options!.map((option) => (
              <div
                key={`${checkBoxGroup.id}${option.id}`}
                className={styles.option}
              >
                <Checkbox
                  itemKey={`${checkBoxGroup.id}${option.id}`}
                  onChange={() =>
                    selectUniqueFilter(checkBoxGroup.id, option.id)
                  }
                  isChecked={option.isSelected}
                />
                <HelperText
                  className="text__body__regular__small__gray700"
                  content={option.name}
                />
              </div>
            ))}
          </div>
        )}
      </Fragment>
    ))}
  </div>
);

type CollaboratorsFiltersProps = {
  className?: string;
  notifyAllNewFiltersSelected: (checkBoxGroupEdited: CheckboxGroupType) => void;
};

const CollaboratorsFilters: React.FC<CollaboratorsFiltersProps> = ({
  notifyAllNewFiltersSelected,
  className,
}) => {
  const {
    state: { checkboxFilters },
    dispatch: collaboratorsDispatch,
  } = useContext(CollaboratorsContext);

  const [showFilterContributorsOptions, setShowFilterContributorsOptions] =
    useState<boolean>(false);
  const [showBottomSheet, setShowBottomSheet] = useState(false);

  const [visibleFilters, setVisibleFilters] = useState(
    checkboxFilters.map(() => false),
  );

  const isTablet = useMediaQuery(`(max-width: ${Breakpoints.lg}px)`);
  const isMobile = useMediaQuery(`(max-width: ${Breakpoints.xs}px)`);

  const containerContriButorsFilterOptionsRef = useRef(null);
  useClickOutside(containerContriButorsFilterOptionsRef, () => {
    setShowFilterContributorsOptions(false);
  });

  const filtersSelected = checkboxFilters.reduce(
    (acc: CheckboxOptionSimpleType[], group) => [
      ...acc,
      ...group.options.filter((opt) => opt.isSelected),
    ],
    [],
  );
  const totalAmountOfFilters = checkboxFilters.reduce(
    (acc, group) => acc + group.options.length,
    0,
  );
  const isAnyFilterApplied =
    filtersSelected.length > 0 && filtersSelected.length < totalAmountOfFilters;

  const clearFilters = () => {
    LocalStorageApi.remove('searchValue');
    LocalStorageApi.remove('sortBy');
    LocalStorageApi.remove('collaboratorsFilters');
    collaboratorsDispatch({
      type: collaboratorsActions.UPDATE_CHECKBOX_FILTERS,
      checkboxFilters: checkboxFilters.map((group) => ({
        ...group,
        options: group.options.map((opt) => ({
          ...opt,
          isSelected: false,
        })),
      })),
    });
    collaboratorsDispatch({
      type: collaboratorsActions.UPDATE_QUERY_PARAMS,
      queryParams: new CollaboratorsQueryParams(),
    });
  };

  const allOptionsSelected = (checkBoxGroupChanged: CheckboxGroupType) =>
    checkBoxGroupChanged.options.every(
      (opt: CheckboxOptionSimpleType) => opt.isSelected === true,
    );

  const noOptionsSelected = (checkBoxGroupChanged: CheckboxGroupType) =>
    checkBoxGroupChanged.options.every(
      (opt: CheckboxOptionSimpleType) => opt.isSelected === false,
    );

  const selectUniqueFilter = (
    checkBoxGroupSelectedId: string,
    filterSelected: string,
  ) => {
    const checkBoxFiltersCopy = cloneDeep(checkboxFilters);

    const checkBoxGroupSelected = checkBoxFiltersCopy.find(
      (group) => group.id === checkBoxGroupSelectedId,
    )!;

    const option = checkBoxGroupSelected.options.find(
      (opt) => opt.id === filterSelected,
    )!;
    option.isSelected = !option.isSelected;

    if (allOptionsSelected(checkBoxGroupSelected)) {
      checkBoxGroupSelected.checkValue = true;
    } else if (noOptionsSelected(checkBoxGroupSelected)) {
      checkBoxGroupSelected.checkValue = false;
    } else {
      checkBoxGroupSelected.checkValue = null;
    }

    LocalStorageApi.set('collaboratorsFilters', checkBoxFiltersCopy);

    collaboratorsDispatch({
      type: collaboratorsActions.UPDATE_CHECKBOX_FILTERS,
      checkboxFilters: checkBoxFiltersCopy,
    });

    notifyAllNewFiltersSelected(checkBoxGroupSelected);
  };

  const showCollaboratorsFilterMenu = () => (
    <div className={styles.contributorsFilterMenu}>
      <Menu
        className={styles.filtersSection}
        width={styles.menuWith}
        position={PositionType.BottomLeft}
      >
        <div className={styles.titleAndClear}>
          <span className="text__body__semi__bold__medium__gray700">
            Filter by
          </span>
          {isAnyFilterApplied && (
            <button
              type="button"
              className={styles.clearFilters}
              onClick={clearFilters}
            >
              <CrossIcon />
              <span className="text__body__semi__bold__tiny__brand500">
                Clear filters
              </span>
            </button>
          )}
        </div>
        <FiltersGroups
          checkBoxFilters={checkboxFilters}
          selectUniqueFilter={selectUniqueFilter}
          visibleFilters={visibleFilters}
          setVisibleFilters={setVisibleFilters}
          isAnyFilterApplied={isAnyFilterApplied}
        />
      </Menu>
    </div>
  );

  if (isTablet || isMobile) {
    return (
      <>
        <div ref={containerContriButorsFilterOptionsRef}>
          <Button
            className={styles.filterButton}
            buttonStyle={ButtonStyle.PrimaryStroke}
            buttonSize={ButtonSize.Small}
            onClick={() => {
              if (isMobile) {
                setShowBottomSheet(true);
              } else {
                setShowFilterContributorsOptions(
                  !showFilterContributorsOptions,
                );
              }
            }}
          >
            {isAnyFilterApplied ? (
              <CounterBadge amount={filtersSelected.length} />
            ) : (
              <FilterLinesIcon />
            )}
            Filters
          </Button>
          {showFilterContributorsOptions && showCollaboratorsFilterMenu()}
        </div>
        <BottomSheet
          className={styles.bottomSheet}
          open={showBottomSheet}
          onDismiss={() => setShowBottomSheet(false)}
        >
          <div className={styles.bottomSheetContent}>
            <div className={styles.bottomSheetHeader}>
              <span className="text__body__semi__bold__medium__gray800">
                Filter by
              </span>
              <button
                type="button"
                onClick={() => {
                  setShowBottomSheet(false);
                  setVisibleFilters(checkboxFilters.map(() => false));
                }}
              >
                <CrossIcon />
              </button>
            </div>
            <div className={styles.bottomSheetSeparator} />
            {isAnyFilterApplied && (
              <button
                type="button"
                className={classnames(
                  styles.clearFilters,
                  styles.bottomSheetClearFilters,
                )}
                onClick={clearFilters}
              >
                <CrossIcon />
                <span className="text__body__semi__bold__tiny__brand500">
                  Clear filters
                </span>
              </button>
            )}
            <FiltersGroups
              checkBoxFilters={checkboxFilters}
              selectUniqueFilter={selectUniqueFilter}
              visibleFilters={visibleFilters}
              setVisibleFilters={setVisibleFilters}
              isAnyFilterApplied={isAnyFilterApplied}
            />
          </div>
        </BottomSheet>
      </>
    );
  }

  return (
    <div className={classnames(className ?? '', styles.container)}>
      <div className={styles.titleAndClear}>
        <span className="text__body__semi__bold__medium__gray700">
          Filter by
        </span>
        {isAnyFilterApplied && (
          <button
            type="button"
            className={styles.clearFilters}
            onClick={clearFilters}
          >
            <CrossIcon />
            <span className="text__body__semi__bold__tiny__brand500">
              Clear filters
            </span>
          </button>
        )}
      </div>
      <FiltersGroups
        checkBoxFilters={checkboxFilters}
        selectUniqueFilter={selectUniqueFilter}
        visibleFilters={visibleFilters}
        setVisibleFilters={setVisibleFilters}
        isAnyFilterApplied={isAnyFilterApplied}
      />
    </div>
  );
};

export { CollaboratorsFilters };
